import React, { useState, useEffect, useRef } from "react";
import { Button, Grid, Stack, RadioGroup, Radio, FormControlLabel, Paper } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CustomInput from "../inputFields/CustomInput";
import PackageInfo from "./packageOptions/PackageInfo";
import AllPackageOptions from "./packageOptions/AllPackageOptions";
import PackageOptionsDetails from "./packageOptions/PackageOptionsDetails";
import DefineIndividualPieces from "./packageOptions/DefineIndividulePieces";
import "animate.css/animate.css";
import httpRequest from "../../../HttpService";
import AccordionNextButton from "../AccordionNextButton";

import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import SyncIcon from '@mui/icons-material/Sync';
import WarningIcon from '@mui/icons-material/Warning';
import CircularProgress from '@mui/material/CircularProgress';

const PackageOptionsAndDetails = (props) => {
    const [info, setInfo] = useState(props.info);
    const [packageDetail, setPackageDetail] = useState({});
    const [defineIndividual, setDefineIndividual] = useState([]);
    const defineIndividualRef = useRef([]);
    const [isLoading, setIsLoading] = useState(true);
    const [packageOptionList, setPackageOptionList] = useState([]);
    const [validation, setValidation] = useState({ disabled: true, errorMessages: [] });
    const [hasManualEdits, setHasManualEdits] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [showPiecesEntry, setShowPiecesEntry] = useState(false);
    const [isMetric, setIsMetric] = useState(false);

    // Derive pieceDescriptionOverride from the currently selected package option
    const selectedPackageForLabel = packageOptionList.find(p => p.iconTitleText === packageDetail.packageSize);
    const pieceDescriptionOverride = selectedPackageForLabel?.pieceDescriptionOverride || '';

    // Propagate override to parent in an effect so the click handler that selects
    // a package isn't interleaved with an extra parent state update (which races
    // with the focus-pieces timeout that fires post-click).
    useEffect(() => {
        if (props.info[2]?.pieceDescriptionOverride !== pieceDescriptionOverride) {
            props.onChange({ target: "pieceDescriptionOverride", value: pieceDescriptionOverride });
        }
    }, [pieceDescriptionOverride]);
    
    const isMounted = useRef(true);
    const attemptCount = useRef(0);
    const maxAttempts = 5; 
    const pollInterval = 1500;
    const isInitialMount = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!isMounted.current) return;
        
        setInfo(props.info);
        if (props.completed.get(0) && props.completed.get(1)) {
            console.log("Package options step active - route changed or component mounted");
            console.log("Current props.info[2]:", props.info[2]);
            
            const hasExistingPackageData = props.info[2] && 
                                           props.info[2].packageSize && 
                                           props.info[2].packageSize !== "";
            
            if (hasExistingPackageData) {
                console.log("Found existing package data, preserving:", props.info[2]);
                setPackageDetail(prev => ({
                    ...prev,
                    packageSize: props.info[2].packageSize,
                    pieces: props.info[2].pieces || prev.pieces || 0,
                    weight: props.info[2].weight || prev.weight || 0,
                    overrideBillWeight: props.info[2].overrideBillWeight || '',
                    additionalDescription: props.info[2].additionalDescription || "",
                }));
                
                if (props.info[2].defineIndividual) {
                    setDefineIndividual(props.info[2].defineIndividual);
                } else if (props.info[2].piecesJSON) {
                    try {
                        const rawPieces = JSON.parse(props.info[2].piecesJSON);
                        // Add id field to each piece if it doesn't exist
                        const parsedPieces = rawPieces.map((piece, index) => ({
                            id: piece.id || index + 1,
                            ...piece
                        }));
                        setDefineIndividual(parsedPieces);
                    } catch (e) {
                        console.error("Error parsing piecesJSON:", e);
                    }
                }
                
                setHasManualEdits(true);
                
                props.onAlert({ "target": "packageSize", "value": false });
                props.onAlert({ "target": "packageDesc", "value": false });
                props.onAlert({ "target": "pieces", "value": false });
                props.onAlert({ "target": "weight", "value": false });
            } else {
                console.log("No existing package data found, using defaults");
                setPackageDetail({
                    packageSize: "",
                    packageDesc: "",
                    pieces: hasManualEdits ? packageDetail.pieces : 0,
                    weight: hasManualEdits ? packageDetail.weight : 0,
                    overrideBillWeight: '',
                    packageImage: null,
                    additionalDescription: ""
                });
                setDefineIndividual([]);
                setShowPiecesEntry(false);
                
                props.onAlert({ "target": "packageSize", "value": true });
                props.onAlert({ "target": "packageDesc", "value": true });
                props.onAlert({ "target": "pieces", "value": true });
                props.onAlert({ "target": "weight", "value": true });
            }
            
            setIsLoading(true);
            setApiError(false);
            setProcessingMessage("Calculating route and preparing package options...");
            attemptCount.current = 0;
            
            if (props.info[0].quoteId !== undefined) {
                pollForPackageOptions();
                props.onAlert({ "target": "Initial", "value": false });
            }
        }
    }, [props.completed.get(1)]);

    const pollForPackageOptions = () => {
        if (!isMounted.current) return;
        
        attemptCount.current += 1;
        console.log(`Polling for package options, attempt ${attemptCount.current}/${maxAttempts}`);
        
        if (attemptCount.current > 1) {
            setProcessingMessage(`Processing route data... (attempt ${attemptCount.current}/${maxAttempts})`);
        }
        
        httpRequest.getQuoteDescriptions(info[0].quoteId)
            .then((response) => {
                if (!isMounted.current) return;
                
                console.log(`Attempt ${attemptCount.current} response:`, response);
                
                const hasRealOptions = response.data && 
                                       response.data.length > 0 && 
                                       response.data[0].descriptionID !== undefined;
                
                if (hasRealOptions) {
                    console.log(`Success! Got ${response.data.length} package options`);
                    processPackageOptions(response.data);
                    return;
                }
                
                const isCalculatingDistance = response.data && 
                                             response.data.length === 1 && 
                                             typeof response.data[0][""] === 'string' &&
                                             response.data[0][""].includes('maps.googleapis.com');
                
                if (isCalculatingDistance) {
                    console.log("Distance calculation in progress, continuing to poll");
                    setProcessingMessage("Calculating delivery route distance...");
                }
                
                if (attemptCount.current < maxAttempts) {
                    setTimeout(() => {
                        if (isMounted.current) {
                            pollForPackageOptions();
                        }
                    }, pollInterval);
                } else {
                    console.log("Maximum polling attempts reached");
                    if (isMounted.current) {
                        setApiError(true);
                        setIsLoading(false);
                    }
                }
            })
            .catch((error) => {
                if (!isMounted.current) return;
                console.error("Error fetching package options:", error);
                
                if (attemptCount.current < maxAttempts) {
                    setTimeout(() => {
                        if (isMounted.current) {
                            pollForPackageOptions();
                        }
                    }, pollInterval);
                } else {
                    if (isMounted.current) {
                        setApiError(true);
                        setIsLoading(false);
                    }
                }
            });
    };

    const processPackageOptions = (data) => {
        if (!isMounted.current) return;
        
        console.log(`🚚🚚🚚 PROCESS PACKAGE OPTIONS START 🚚🚚🚚`);
        console.log(`📥 Raw API data:`, data);
        console.log(`📋 Current props.info[2]:`, props.info[2]);
        console.log(`📦 Current packageDetail:`, packageDetail);
        
        const tempOptionList = [];
        
        data.forEach((e, i) => {
            const option = tempOptionList.find((obj) => obj.descriptionID === e.descriptionID);
            if (option === undefined) {
                tempOptionList.push({
                    ...e,
                    id: i + 1,
                });
            }
        });
        
        console.log(`🎯 Available package options:`, tempOptionList.map(opt => opt.iconTitleText));
        
        setPackageOptionList(tempOptionList);



        if (props.info[2] && props.info[2].packageSize) {
            console.log(`✅ QUOTE DATA FOUND - Preserving:
Package: ${props.info[2].packageSize}
Pieces: ${props.info[2].pieces}
Weight: ${props.info[2].weight}
Override Bill Weight: ${props.info[2].overrideBillWeight || 'None'}`);
            
            const selectedOption = tempOptionList.find(e => e.iconTitleText === props.info[2].packageSize);
            console.log(`🔍 Matching option search result:`, selectedOption ? `FOUND: ${selectedOption.iconTitleText}` : `❌ NOT FOUND IN AVAILABLE OPTIONS`);
            
            if (selectedOption) {
                const newPackageDetail = {
                    packageSize: props.info[2].packageSize,
                    packageDesc: selectedOption.iconTitleText,
                    packageImage: selectedOption.icon_url,
                    pieces: props.info[2].pieces,
                    weight: props.info[2].weight,
                    overrideBillWeight: props.info[2].overrideBillWeight || '',
                    additionalDescription: props.info[2].additionalDescription || ""
                };
                console.log(`📦 Setting NEW packageDetail:`, newPackageDetail);
                setPackageDetail(newPackageDetail);

                const shouldShow = selectedOption.showPiecesEntry === true || selectedOption.showPiecesEntry === 1;
                setShowPiecesEntry(shouldShow);

                setHasManualEdits(true);
                console.log(`✅ Package data preserved and hasManualEdits set to true`);
            } else {
                console.log(`❌ PACKAGE NOT FOUND - "${props.info[2].packageSize}" not in available options`);
            }
        } else {
            console.log(`❌ NO QUOTE DATA FOUND in props.info[2]`);
        }

        setApiError(false);
        setIsLoading(false);
        console.log(`🚚🚚🚚 PROCESS PACKAGE OPTIONS END 🚚🚚🚚`);
    };

    const handleChange = (event) => {
        const { name } = event.target;
        let { value } = event.target;

        if (name === "pieces" || name === "weight") {
            const { totalTableWeight, newPieces } = defineIndividual.reduce((acc, piece) => {
                const quantity = parseInt(piece.quantity, 10) || 0;
                acc.totalTableWeight += (parseFloat(piece.weight) || 0) * quantity;
                acc.newPieces += quantity;
                return acc;
            }, { totalTableWeight: 0, newPieces: 0 });

            if (name === "pieces" && (parseInt(value, 10) || 0) < newPieces) {
                value = newPieces;
            }
            if (name === "weight" && (parseFloat(value) || 0) < totalTableWeight) {
                value = totalTableWeight;
            }
        }

        if ((name === "pieces" || name === "weight" || name === "overrideBillWeight") && 
            (event.target.manualInput || event.target.isManualEdit)) {
            setHasManualEdits(true);
        }
        
        if (name === "packageSize") {
            const selectedPackage = packageOptionList.find(p => p.iconTitleText === value);
            if (selectedPackage) {
                setShowPiecesEntry(selectedPackage.showPiecesEntry === true || selectedPackage.showPiecesEntry === 1);

                // Apply the new package's defaults only if the user hasn't
                // typed pieces/weight yet. Preserves the user's manual entry
                // when they switch packages — they don't have to re-type the
                // same number after realizing they picked the wrong tile.
                if (!hasManualEdits) {
                    setTimeout(() => {
                        const defaultPieces = selectedPackage.fillPieces !== undefined ? selectedPackage.fillPieces : 1;
                        const defaultWeight = selectedPackage.fillWeight !== undefined ? selectedPackage.fillWeight : 0;

                        setPackageDetail(prev => ({
                            ...prev,
                            packageSize: value,
                            pieces: defaultPieces,
                            weight: defaultWeight,
                        }));

                        props.onChange({ "target": "packageSize", "value": value });
                        props.onChange({ "target": "pieces", "value": defaultPieces });
                        props.onChange({ "target": "weight", "value": defaultWeight });
                        props.setCompletedStep(2);
                    }, 0);
                    return;
                }
            } else {
                setShowPiecesEntry(false);
            }
        }
        
        setPackageDetail((prevState) => ({ ...prevState, [name]: value }));
        props.onChange({ "target": name, "value": value });
        if (value !== null && value !== undefined) {
            props.onAlert({ "target": name, "value": false });
        }

        props.setCompletedStep(2);
    };

    const handleSyncTotals = () => {
        const { totalTableWeight, newPieces } = defineIndividual.reduce((acc, piece) => {
            const quantity = parseInt(piece.quantity, 10) || 0;
            acc.totalTableWeight += (parseFloat(piece.weight) || 0) * quantity;
            acc.newPieces += quantity;
            return acc;
        }, { totalTableWeight: 0, newPieces: 0 });

        setPackageDetail(prev => {
            const newWeight = hasManualEdits ? Math.max(prev.weight, totalTableWeight) : totalTableWeight;
            const newTotalPieces = hasManualEdits ? Math.max(prev.pieces, newPieces) : newPieces;
            
            if (newWeight !== prev.weight) {
                props.onChange({ "target": "weight", "value": newWeight });
            }
            if (newTotalPieces !== prev.pieces) {
                props.onChange({ "target": "pieces", "value": newTotalPieces });
            }

            return {
                ...prev,
                weight: newWeight,
                pieces: newTotalPieces
            };
        });
    };

    const handleRetryFetch = () => {
        console.log("Manual retry requested");
        setApiError(false);
        setIsLoading(true);
        setProcessingMessage("Retrying package options retrieval...");
        
        attemptCount.current = 0;
        
        pollForPackageOptions();
    };

    const handleNextPanel = async () => {
        console.log("Next panel requested with package details", {
            packageSize: packageDetail.packageSize,
            pieces: packageDetail.pieces,
            weight: packageDetail.weight,
            overrideBillWeight: packageDetail.overrideBillWeight
        });

        let piecesOption = [];
        defineIndividualRef.current.forEach((item) => {
            const itemCopy = { ...item };
            delete itemCopy.id;
            piecesOption.push(itemCopy);
        });

        // Compute effective totals from the ref so that values entered in the
        // grid without tabbing away (click-Next immediately) are included even
        // before the useEffect([defineIndividual]) → handleSyncTotals chain runs.
        let effectiveWeight = parseFloat(packageDetail.weight) || 0;
        let effectivePieces = parseInt(packageDetail.pieces, 10) || 0;
        if (defineIndividualRef.current.length > 0) {
            const { totalTableWeight, newPieces } = defineIndividualRef.current.reduce((acc, piece) => {
                const quantity = parseInt(piece.quantity, 10) || 0;
                acc.totalTableWeight += (parseFloat(piece.weight) || 0) * quantity;
                acc.newPieces += quantity;
                return acc;
            }, { totalTableWeight: 0, newPieces: 0 });
            effectiveWeight = hasManualEdits ? Math.max(effectiveWeight, totalTableWeight) : totalTableWeight;
            effectivePieces = hasManualEdits ? Math.max(effectivePieces, newPieces) : newPieces;
        }

        try {
            var packOption = packageOptionList.find(e => e.iconTitleText === packageDetail.packageSize);
            if (packOption !== undefined) {
                console.log("Posting quote descriptions with options", {
                    descriptionId: packOption.descriptionID,
                    overrideBillWeight: packageDetail.overrideBillWeight
                });

                let res = await httpRequest.postQuoteDescriptions(
                    props.info[0].quoteId,
                    packOption.descriptionID,
                    effectivePieces,
                    effectiveWeight,
                    packageDetail.additionalDescription,
                    piecesOption.length > 0 ? JSON.stringify(piecesOption) : "",
                    packageDetail.overrideBillWeight || null
                );

                console.log("Post response", res.data);

                props.onChange({
                    "target": "quoteDescriptions_data",
                    "value": {
                        pieces: effectivePieces,
                        weight: effectiveWeight,
                        overrideBillWeight: packageDetail.overrideBillWeight,
                        piecesJSON: piecesOption.length > 0 ? JSON.stringify(piecesOption) : null
                    }
                });
                props.onNextPanel();
            }
        } catch (error) {
            console.error("Error posting quote descriptions", error);
            alert("There was an error saving the package description. Please try again or contact support.");
        }
    };

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            handleSyncTotals();
        }
    }, [defineIndividual]);

    useEffect(() => {
        const { packageSize, pieces, weight, overrideBillWeight } = packageDetail;
        let errorMessages = [];
        const selectedPackage = packageOptionList.find(p => p.iconTitleText === packageSize);

        if (!packageSize) {
            errorMessages.push("Please select a package type.");
        }

        if (selectedPackage) {
            const { validateMaxWeight, validateMinWeight, validateMaxSinglePieceWeight } = selectedPackage;
            const individualPiecesWeight = defineIndividual.reduce((sum, piece) => sum + Number(piece.weight || 0), 0);
            const totalWeight = Number(weight) || individualPiecesWeight;
            const numPieces = Number(pieces) || 0;

            const hasNegativeValues = defineIndividual.some(item => 
                (Number(item.quantity) < 0) ||
                (Number(item.length) < 0) ||
                (Number(item.width) < 0) ||
                (Number(item.height) < 0) ||
                (Number(item.weight) < 0) ||
                (Number(item.positions) < 0)
            ) || numPieces < 0 || totalWeight < 0;

            if (hasNegativeValues) {
                errorMessages.push("Negative values are not allowed.");
            }

            if (!numPieces > 0) {
                errorMessages.push("Please enter a valid number of pieces.");
            }

            if (!totalWeight > 0) {
                errorMessages.push("Please enter a valid weight.");
            } else {
                // Check total weight constraints
                if (validateMaxWeight && totalWeight > validateMaxWeight) {
                    errorMessages.push(`Total weight cannot exceed ${validateMaxWeight} lbs`);
                } 
                if (validateMinWeight && totalWeight < validateMinWeight) {
                    errorMessages.push(`Total weight must be at least ${validateMinWeight} lbs`);
                }

                // Check single piece weight constraint
                if (validateMaxSinglePieceWeight) {
                    
                    // If user defined individual pieces, check each piece
                    if (defineIndividual.length > 0) {
                        const overweightPieces = defineIndividual.filter(piece => {
                            const pieceWeight = parseFloat(piece.weight) || 0;
                            return pieceWeight > validateMaxSinglePieceWeight;
                        });
                        
                        if (overweightPieces.length > 0) {
                            const maxOverweight = Math.max(...overweightPieces.map(p => parseFloat(p.weight) || 0));
                            errorMessages.push(`Individual piece weight (${maxOverweight} lbs) exceeds maximum allowed (${validateMaxSinglePieceWeight} lbs)`);
                        }
                    }
                    // If user entered pieces = 1 AND did NOT define individual pieces, check if that single piece exceeds max
                    else if (numPieces === 1 && totalWeight > validateMaxSinglePieceWeight) {
                        errorMessages.push(`Single piece weight (${totalWeight} lbs) exceeds maximum allowed (${validateMaxSinglePieceWeight} lbs)`);
                    }
                }
            }
        }

        setValidation({ disabled: errorMessages.length > 0, errorMessages });
    }, [packageDetail, packageOptionList, defineIndividual]);

    return (
        <Grid container style={{ paddingLeft: "1%", paddingRight: "1%" }}>
            <Grid item xs={12}>
                <Grid container>
                    {isLoading ? (
                        <Grid item xs={12} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px' }}>
                            <CircularProgress />
                            <Typography variant="body2" style={{ marginTop: '15px', color: '#666', textAlign: 'center' }}>
                                {processingMessage}
                            </Typography>
                            <Typography variant="caption" style={{ marginTop: '8px', color: '#999', fontSize: '0.8rem', textAlign: 'center' }}>
                                {props.info[1]?.pickUpLocation?.companyName} to {props.info[1]?.deliveryLocation?.companyName}
                            </Typography>
                        </Grid>
                    ) : apiError ? (
                        <Grid item xs={12} style={{ marginBottom: '20px' }}>
                            <div style={{ 
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffeeba',
                                borderRadius: '4px',
                                padding: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#856404'
                            }}>
                                <WarningIcon style={{ marginRight: '15px', color: '#ffc107' }} />
                                <div style={{ flex: 1 }}>
                                    <Typography variant="body1" style={{ fontWeight: 500, marginBottom: '8px' }}>
                                        No package options available
                                    </Typography>
                                    <Typography variant="body2">
                                        We couldn't retrieve package options for this route. This may be because this is a 
                                        new route that requires additional processing. Try clicking the Retry button.
                                    </Typography>
                                </div>
                                <Button 
                                    variant="outlined" 
                                    color="primary" 
                                    onClick={handleRetryFetch}
                                    style={{ marginLeft: '16px' }}
                                >
                                    Retry
                                </Button>
                            </div>
                        </Grid>
                    ) : (
                        packageOptionList.length > 0 && (
                            <Grid item sm={10} md={10}>
                                <AllPackageOptions
                                    accountNumber={info[0].accountId}
                                    packageSize={packageDetail.packageSize}
                                    onChange={handleChange}
                                    packageOptionList={packageOptionList}
                                    hasManualEdits={hasManualEdits}
                                    isLoading={false}
                                />
                            </Grid>
                        )
                    )}
                    
                    <Grid item sm={10} md={10}>
                        {!isLoading && packageOptionList.length > 0 && (
                            <Paper variant="outlined" style={{ padding: '12px 16px', marginTop: 16, borderRadius: 8 }}>
                                {showPiecesEntry ? (
                                    <>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
                                            <Typography color="primary" style={{ fontWeight: 500 }}>
                                                Define Individual Pieces (Optional)
                                            </Typography>
                                            <Stack direction="row" alignItems="center">
                                                <Typography variant="caption" color="textSecondary" style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
                                                    Units:
                                                </Typography>
                                                <RadioGroup
                                                    row
                                                    value={isMetric ? 'metric' : 'imperial'}
                                                    onChange={(e) => setIsMetric(e.target.value === 'metric')}
                                                >
                                                    <FormControlLabel
                                                        value="imperial"
                                                        control={<Radio size="small" sx={{ padding: '2px 4px' }} />}
                                                        label="Imperial"
                                                        sx={{ marginRight: '8px', '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
                                                    />
                                                    <FormControlLabel
                                                        value="metric"
                                                        control={<Radio size="small" sx={{ padding: '2px 4px' }} />}
                                                        label="Metric"
                                                        sx={{ marginRight: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
                                                    />
                                                </RadioGroup>
                                            </Stack>
                                        </Stack>
                                        <DefineIndividualPieces
                                            setDefineIndividual={(items) => {
                                                defineIndividualRef.current = items;
                                                setDefineIndividual(items);
                                            }}
                                            initialData={defineIndividual}
                                            isMetric={isMetric}
                                        />
                                        <div style={{ borderTop: '1px solid rgba(0,0,0,0.12)', margin: '12px 0' }} />
                                    </>
                                ) : (
                                    <Stack direction="row" alignItems="center" style={{ marginBottom: 8 }}>
                                        <Typography variant="caption" color="textSecondary" style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
                                            Units:
                                        </Typography>
                                        <RadioGroup
                                            row
                                            value={isMetric ? 'metric' : 'imperial'}
                                            onChange={(e) => setIsMetric(e.target.value === 'metric')}
                                        >
                                            <FormControlLabel
                                                value="imperial"
                                                control={<Radio size="small" sx={{ padding: '2px 4px' }} />}
                                                label="Imperial"
                                                sx={{ marginRight: '8px', '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
                                            />
                                            <FormControlLabel
                                                value="metric"
                                                control={<Radio size="small" sx={{ padding: '2px 4px' }} />}
                                                label="Metric"
                                                sx={{ marginRight: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
                                            />
                                        </RadioGroup>
                                    </Stack>
                                )}
                                <PackageInfo
                                    accountNumber={info[0].accountId}
                                    packageOptionList={packageOptionList}
                                    packageSize={packageDetail.packageSize}
                                    packageDesc={packageDetail.packageDesc}
                                    pieces={packageDetail.pieces}
                                    weight={packageDetail.weight}
                                    weightUnit={props.info[2].weightUnit || 'lbs'}
                                    isMetric={isMetric}
                                    overrideBillWeight={packageDetail.overrideBillWeight}
                                    additionalDescription={packageDetail.additionalDescription}
                                    onChange={handleChange}
                                    defineIndividual={defineIndividual}
                                    isEmbedded={props.isEmbedded}
                                    pieceDescriptionOverride={pieceDescriptionOverride}
                                />
                            </Paper>
                        )}
                    </Grid>
                    
                    <Grid item xs={12}>
                        <AccordionNextButton
                            onClick={handleNextPanel}
                            disabled={validation.disabled || isLoading}
                            panelIndex={2}
                            activePanel={props.expanded}
                            errorMessage={validation.errorMessages.join('\n')}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default PackageOptionsAndDetails;